import React from 'react';
import { Radio, Users } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useRealtime } from '@/context/RealtimeContext';

const initials = (name) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** Small "who is here" pill, shown in the app shell so it is available on every screen. */
export const PresenceBar = ({ collapsed = false }) => {
  const { online, connected, me } = useRealtime();
  const others = online.filter((u) => u.email !== me?.email);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="w-full flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-left hover:bg-muted transition-colors"
          data-testid="presence-pill"
          title={connected ? 'Live updates are on' : 'Reconnecting…'}
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            {connected && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
            )}
            <span
              className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                connected ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
            />
          </span>
          {!collapsed && (
            <span className="text-xs font-medium truncate" data-testid="presence-count">
              {connected ? `${online.length} online` : 'Reconnecting…'}
            </span>
          )}
          {!collapsed && <Users className="h-3.5 w-3.5 ml-auto text-muted-foreground" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64" data-testid="presence-panel">
        <div className="flex items-center gap-2 mb-3">
          <Radio className={`h-4 w-4 ${connected ? 'text-emerald-500' : 'text-amber-500'}`} />
          <p className="text-sm font-semibold">
            {connected ? 'Live updates on' : 'Reconnecting…'}
          </p>
        </div>
        <div className="space-y-2">
          {online.map((user) => (
            <div key={user.email} className="flex items-center gap-2" data-testid={`presence-user-${user.email}`}>
              <span className="h-7 w-7 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-200 text-[10px] font-bold flex items-center justify-center">
                {initials(user.name || user.email)}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">
                  {user.name}
                  {user.email === me?.email && ' (you)'}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          ))}
          {others.length === 0 && (
            <p className="text-xs text-muted-foreground">Nobody else is in the CRM right now.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PresenceBar;
